import { useState } from 'react';
import { addMonths, format, startOfDay, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { MonthGrid } from './month-grid';

const KEY = 'yyyy-MM-dd';
const COMPLETE_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** `yyyy-MM-dd` at local noon: the same convention `format.ts` reads and writes. */
const parseValue = (value: string): Date | null => {
  if (!value) return null;
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * The day a stored instant falls on *here*, ready for this control.
 *
 * Slicing the ISO string reads the day in UTC, and a follow-up saved for the
 * end of a Chilean day is already tomorrow over there: the field would show the
 * date one day after the one that was just chosen.
 */
export function toDayInput(stored: string | null | undefined): string {
  if (!stored) return '';
  if (COMPLETE_DAY.test(stored)) return stored;
  const parsed = new Date(stored);
  return Number.isNaN(parsed.getTime()) ? '' : format(parsed, KEY);
}

export interface DateFieldProps {
  id?: string;
  /** `yyyy-MM-dd`, empty when there is no date yet. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** An optional date needs a way back to empty; a required one must not offer it. */
  clearable?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
  placeholder?: string;
  className?: string;
}

/**
 * Una fecha, escrita como la escribe el producto.
 *
 * `<input type="date">` renders in the operating system's locale — «07/14/2026»
 * on a machine set to English — thirty pixels away from our own «14 jul 2026»,
 * and with a calendar we cannot make Spanish. This is the same control
 * everywhere: a button that reads the date out, and a month grid behind it.
 */
export function DateField({
  id,
  value,
  onChange,
  disabled = false,
  clearable = false,
  placeholder = 'Elige una fecha',
  className,
  ...aria
}: DateFieldProps) {
  const selected = parseValue(value);
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => startOfMonth(selected ?? new Date()));

  // Picking the day that is already there is not an edit: letting it through
  // costs a save, and every save bumps the record's `updatedAt`.
  const commit = (next: string) => {
    setOpen(false);
    if (next !== value) onChange(next);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        // Reopening on the month of the date it is showing, not on the month
        // the person happened to browse to last time.
        if (next) setMonth(startOfMonth(selected ?? new Date()));
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          disabled={disabled}
          className={cn('w-full justify-start gap-2 font-normal', className)}
          {...aria}
        >
          <CalendarDays className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          {selected ? (
            <span className="truncate tabular">{format(selected, 'd MMM yyyy', { locale: es })}</span>
          ) : (
            <span className="truncate text-muted-foreground">{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-64 p-2">
        <div className="flex items-center justify-between pb-1">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Mes anterior"
            onClick={() => setMonth(addMonths(month, -1))}
          >
            <ChevronLeft className="size-3.5" />
          </Button>
          <span aria-live="polite" className="text-sm font-medium first-letter:uppercase">
            {format(month, 'LLLL yyyy', { locale: es })}
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Mes siguiente"
            onClick={() => setMonth(addMonths(month, 1))}
          >
            <ChevronRight className="size-3.5" />
          </Button>
        </div>

        <MonthGrid
          month={month}
          selected={selected}
          onSelect={(date) => commit(format(date, KEY))}
          onMonthChange={setMonth}
        />

        <div className="mt-1 flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="flex-1 justify-center"
            onClick={() => commit(format(startOfDay(new Date()), KEY))}
          >
            Hoy
          </Button>
          {clearable && value !== '' && (
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 justify-center text-muted-foreground"
              onClick={() => commit('')}
            >
              Quitar
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
